import {
  BrandWifiProfileConfiguration,
  KioskWifiProfileConfiguration,
  LocationWifiProfileConfiguration,
  ModelNameFlags,
  RoomWifiProfileConfiguration,
} from '@era/settings-sdk';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { CategoryType } from '../shared/types';

export interface WifiProfileConfiguration {
  categoryType?: CategoryType;
  categoryId?: string;
  profileId: string;
  displayName: string;
  modelNames: ModelNameFlags[];
}

type AnyWifiProfileConfiguration =
  | BrandWifiProfileConfiguration
  | LocationWifiProfileConfiguration
  | RoomWifiProfileConfiguration
  | KioskWifiProfileConfiguration;

/**
 * Generic function to convert any wifi profile configuration to the unified format.
 * Automatically determines the category type and ID based on the input configuration.
 */
export function wifiProfileConfigurationFrom(configuration: AnyWifiProfileConfiguration): WifiProfileConfiguration {
  const modelNames = Array.isArray(configuration.models) ? configuration.models : [configuration.models];

  // Type guard functions to determine the category type
  if ('brandId' in configuration) {
    return {
      categoryType: 'Brand',
      categoryId: configuration.brandId,
      profileId: configuration.wifiProfileId,
      displayName: configuration.displayName,
      modelNames,
    };
  }
  if ('locationId' in configuration) {
    return {
      categoryType: 'Location',
      categoryId: configuration.locationId,
      profileId: configuration.wifiProfileId,
      displayName: configuration.displayName,
      modelNames,
    };
  }
  if ('roomId' in configuration) {
    return {
      categoryType: 'Room',
      categoryId: configuration.roomId,
      profileId: configuration.wifiProfileId,
      displayName: configuration.displayName,
      modelNames,
    };
  }
  if ('kioskId' in configuration) {
    return {
      categoryType: 'Kiosk',
      categoryId: configuration.kioskId,
      profileId: configuration.wifiProfileId,
      displayName: configuration.displayName,
      modelNames,
    };
  }
  throw new Error('Unsupported Category Type for Wifi Profile Configuration');
}

export type CategoryWifiProfileState = EntityState<WifiProfileConfiguration>;

export function selectId(wifiProfile: WifiProfileConfiguration): string {
  return `${wifiProfile.profileId}-${wifiProfile.categoryType}-${wifiProfile.categoryId}`;
}

export const adapter: EntityAdapter<WifiProfileConfiguration> = createEntityAdapter<WifiProfileConfiguration>({
  selectId,
});

export const initialState: CategoryWifiProfileState = adapter.getInitialState();
